// Ticket Status enum — matches backend integer values
export enum TicketStatus {
  Open = 0,
  InProgress = 1,
  WaitingCustomer = 2,
  Resolved = 3,
  Closed = 4,
}

// Ticket Priority enum
export enum TicketPriority {
  Low = 0,
  Medium = 1,
  High = 2,
  Urgent = 3,
}

// Ticket Category enum
export enum TicketCategory {
  General = 0,
  Technical = 1,
  Billing = 2,
  FeatureRequest = 3,
  Bug = 4,
}

// Ticket DTO
export interface Ticket {
  id: string;
  tenantId: string;
  createdByUserId: string;
  assignedToUserId: string | null;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  resolvedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  createdByUserName: string | null;
  createdByUserEmail: string | null;
  assignedToUserName: string | null;
  tenantName: string | null;
  messages: TicketMessage[];
  messageCount: number;
}

// Ticket List DTO (for table views)
export interface TicketListItem {
  id: string;
  tenantId: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  createdAt: string;
  updatedAt: string | null;
  createdByUserName: string | null;
  assignedToUserName: string | null;
  tenantName: string | null;
  messageCount: number;
}

// Ticket Message DTO
export interface TicketMessage {
  id: string;
  ticketId: string;
  senderUserId: string;
  content: string;
  isInternal: boolean;
  attachmentUrl: string | null;
  senderUserName: string | null;
  senderUserEmail: string | null;
  isFromSupport: boolean;
  createdAt: string;
}

// Create Ticket Request
export interface CreateTicketRequest {
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
}

// Update Status Request
export interface UpdateTicketStatusRequest {
  status: TicketStatus;
}

// Assign Ticket Request
export interface AssignTicketRequest {
  assignedToUserId: string | null;
}

// Update Priority Request
export interface UpdateTicketPriorityRequest {
  priority: TicketPriority;
}

// Create Message Request
export interface CreateTicketMessageRequest {
  content: string;
  isInternal: boolean;
  attachmentUrl?: string | null;
}

// Ticket Stats DTO
export interface TicketStats {
  totalTickets: number;
  openTickets: number;
  inProgressTickets: number;
  waitingCustomerTickets: number;
  resolvedTickets: number;
  closedTickets: number;
  urgentTickets: number;
  highPriorityTickets: number;
}

// Ticket Filter
export interface TicketFilter {
  tenantId?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  category?: TicketCategory;
  assignedToUserId?: string;
}

// API Response wrapper
export interface ApiResponse<T> {
  success: boolean;
  message: string | null;
  data: T;
}

// Status display helpers
export const ticketStatusLabels: Record<TicketStatus, string> = {
  [TicketStatus.Open]: 'Open',
  [TicketStatus.InProgress]: 'In Progress',
  [TicketStatus.WaitingCustomer]: 'Waiting Customer',
  [TicketStatus.Resolved]: 'Resolved',
  [TicketStatus.Closed]: 'Closed',
};

export const ticketPriorityLabels: Record<TicketPriority, string> = {
  [TicketPriority.Low]: 'Low',
  [TicketPriority.Medium]: 'Medium',
  [TicketPriority.High]: 'High',
  [TicketPriority.Urgent]: 'Urgent',
};

export const ticketCategoryLabels: Record<TicketCategory, string> = {
  [TicketCategory.General]: 'General',
  [TicketCategory.Technical]: 'Technical',
  [TicketCategory.Billing]: 'Billing',
  [TicketCategory.FeatureRequest]: 'Feature Request',
  [TicketCategory.Bug]: 'Bug',
};

// Status color helpers (for badges/chips)
export const ticketStatusColors: Record<TicketStatus, string> = {
  [TicketStatus.Open]: 'info',
  [TicketStatus.InProgress]: 'warning',
  [TicketStatus.WaitingCustomer]: 'secondary',
  [TicketStatus.Resolved]: 'success',
  [TicketStatus.Closed]: 'default',
};

export const ticketPriorityColors: Record<TicketPriority, string> = {
  [TicketPriority.Low]: 'default',
  [TicketPriority.Medium]: 'info',
  [TicketPriority.High]: 'warning',
  [TicketPriority.Urgent]: 'error',
};

// All status values for iteration (avoids Object.values on numeric enum)
export const allTicketStatuses = [
  TicketStatus.Open,
  TicketStatus.InProgress,
  TicketStatus.WaitingCustomer,
  TicketStatus.Resolved,
  TicketStatus.Closed,
];
